/*
  mr-scale-calc-ui.js — DOM layer for the Miniature Reality scale calculator.

  Depends on window.MRScaleCalc (assets/mr-scale-calc.js), loaded first.
  All maths lives in that module; this file only reads inputs, writes results,
  and handles buttons, the share URL and analytics.

  Public: window.MRScaleCalcUI.mount(rootElement)
  Auto-mounts every [data-mr-calc] on the page at DOMContentLoaded.
*/
(function () {
  'use strict';

  var C = window.MRScaleCalc;
  if (!C) { return; }

  var DEBOUNCE_MS = 800;
  var SLICER_STORAGE_KEY = 'mr-scale-calc:slicer';

  // localStorage throws outright in some privacy modes — every access is guarded.
  function storedSlicer() {
    try { return window.localStorage.getItem(SLICER_STORAGE_KEY) || ''; } catch (e) { return ''; }
  }
  function storeSlicer(key) {
    try { window.localStorage.setItem(SLICER_STORAGE_KEY, key); } catch (e) {}
  }

  /* ------------------------------------------------------------- helpers */

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function val(el) { return el ? el.value : ''; }
  function setText(el, text) { if (el && el.textContent !== text) { el.textContent = text; } }
  // Config strings only ever reach innerHTML through here.
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function fld(root, name) { return root.querySelector('[data-mr-field="' + name + '"]'); }
  function out(root, name) { return root.querySelector('[data-mr-out="' + name + '"]'); }

  // Analytics — fires through whatever is present, never throws.
  function track(eventName, payload) {
    try {
      if (typeof window.gtag === 'function') { window.gtag('event', eventName, payload); }
    } catch (e) {}
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
    } catch (e2) {}
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e3) {}
  }

  function copyText(text, btn, okLabel) {
    var done = function (ok) {
      if (!btn) { return; }
      var original = btn.getAttribute('data-mr-copy-label') || btn.textContent;
      btn.setAttribute('data-mr-copy-label', original);
      btn.textContent = ok ? (okLabel || 'Copied') : 'Copy failed';
      window.setTimeout(function () { btn.textContent = original; }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () { done(true); }, function () { fallback(); });
    } else { fallback(); }

    function fallback() {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', 'readonly');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        done(ok);
      } catch (e) { done(false); }
    }
  }

  /* --------------------------------------------------------------- mount */

  function mount(root) {
    if (!root || root.__mrScaleCalcMounted) { return null; }
    root.__mrScaleCalcMounted = true;

    var lastMode = 'scale-to-scale';
    var currentMode = 'resize';
    var slicerKey = '';
    var lastResize = null;

    // 'calculator', or whatever ?src= says the visitor arrived from.
    var sourcePage = 'calculator';
    try {
      var srcParam = C.queryToState(window.location.search).src;
      if (srcParam) { sourcePage = String(srcParam).slice(0, 40); }
    } catch (eSrc) {}

    /* ---- printability verdicts ----------------------------------------- */

    // Writes "Prints on: …" into [data-mr-out="<name>"]. Guidance only — the
    // calculation above it always stands, whatever the verdict says.
    function renderVerdict(name, dimsMm) {
      var el = out(root, name);
      if (!el) { return null; }
      var v = C.printability(dimsMm);
      if (!v) {
        el.textContent = '';
        el.className = 'mr-calc__verdict';
        el.hidden = true;
        return null;
      }
      el.hidden = false;
      el.className = 'mr-calc__verdict is-' + v.tone;
      var text = v.summary === 'no'
        ? 'Prints on: no typical home machine'
        : 'Prints on: ' + v.label;
      if (v.reason) { text += ' — ' + v.reason; }
      setText(el, text);
      return v;
    }

    /* ---- CALC 1: scale -> scale ---------------------------------------- */
    function calc1() {
      var fromEl = fld(root, 'c1-from'), toEl = fld(root, 'c1-to');
      if (!fromEl || !toEl) { return; }
      var r = C.scaleToScale(val(fromEl), val(toEl));
      var summary = out(root, 'c1-summary');
      if (!r) {
        setText(out(root, 'c1-percent'), '—');
        setText(out(root, 'c1-factor'), '—');
        setText(out(root, 'c1-direction'), '');
        setText(out(root, 'c1-delta'), '');
        setText(out(root, 'c1-pair'), 'Enter both scales');
        setText(out(root, 'c1-pct-label'), 'result');
        setText(summary, 'Enter both scales to see the slicer percentage.');
        renderPanel('s1', null);
        return;
      }
      var dirWord = r.direction === 'enlarge' ? 'Enlarge' : (r.direction === 'reduce' ? 'Reduce' : 'No change');
      setText(out(root, 'c1-percent'), C.fmtPercent(r.percent));
      setText(out(root, 'c1-pct-label'), C.fmtPercent(r.percent));
      setText(out(root, 'c1-pair'), C.fmtScale(r.current) + ' → ' + C.fmtScale(r.target));
      setText(out(root, 'c1-factor'), '×' + C.round(r.factor, 4));
      setText(out(root, 'c1-direction'), dirWord);
      var d1 = C.deltaPercent(r.percent);
      setText(out(root, 'c1-delta'), d1 ? d1.label : '');
      setText(out(root, 'c1-work'), C.fmtDenominator(r.current) + ' ÷ ' + C.fmtDenominator(r.target) +
        ' = ' + C.round(r.factor, 4) + '  →  ' + C.fmtPercent(r.percent));
      setText(out(root, 'c1-work-dir'), dirWord.toLowerCase());
      setText(summary, 'Scale X, Y and Z uniformly to ' + C.fmtPercent(r.percent) +
        ' (' + C.fmtScale(r.current) + ' → ' + C.fmtScale(r.target) + ').');
      setBrowse('c1', r.target);
      root.__c1 = r;
      renderPanel('s1', {
        headline: C.fmtScale(r.current) + ' → ' + C.fmtScale(r.target),
        percent: r.percent
      });
    }

    /* ---- CALC 2: real object -> model ---------------------------------- */
    function calc2() {
      var unitKey = val(fld(root, 'c2-unit'));
      var scale = val(fld(root, 'c2-scale'));
      var d = C.parseScale(scale);
      setText(out(root, 'c2-scale-echo'), isFinite(d) ? C.fmtScale(d) : '—');
      ['l', 'w', 'h'].forEach(function (axis) {
        var el = fld(root, 'c2-' + axis);
        var r = el ? C.realToModel(val(el), unitKey, scale) : null;
        setText(out(root, 'c2-' + axis + '-mm'), r ? C.fmtMm(r.mm) : '—');
        if (axis === 'l') { setText(out(root, 'c2-l-mm-cell'), r ? C.fmtMm(r.mm) : '—'); }
        setText(out(root, 'c2-' + axis + '-in'), r ? C.fmtIn(r.in) : '—');
      });
      // Verdict uses every axis the visitor filled in.
      var dims = ['l', 'w', 'h'].map(function (axis) {
        var el2 = fld(root, 'c2-' + axis);
        var rr = el2 ? C.realToModel(val(el2), unitKey, scale) : null;
        return rr ? rr.mm : NaN;
      });
      renderVerdict('c2-verdict', dims);

      var summary = out(root, 'c2-summary');
      var lr = C.realToModel(val(fld(root, 'c2-l')), unitKey, scale);
      if (lr && isFinite(d)) {
        setText(summary, 'At ' + C.fmtScale(d) + ', ' + (C.num(val(fld(root, 'c2-l')))) + ' ' +
          unitKey + ' comes out ' + C.fmtMm(lr.mm) + ' (' + C.fmtIn(lr.in) + ') on the print bed.');
      } else {
        setText(summary, 'Enter at least one real measurement and a scale.');
      }
      setBrowse('c2', d);
    }

    /* ---- CALC 3: STL size -> required slicer % ------------------------- */
    function calc3() {
      var r = C.requiredSlicerPercent(
        val(fld(root, 'c3-stl')), val(fld(root, 'c3-stl-unit')),
        val(fld(root, 'c3-real')), val(fld(root, 'c3-real-unit')),
        val(fld(root, 'c3-scale')));
      if (!r) {
        setText(out(root, 'c3-desired-mm'), '—');
        setText(out(root, 'c3-desired-in'), '—');
        setText(out(root, 'c3-percent'), '—');
        setText(out(root, 'c3-delta'), '');
        setText(out(root, 'c3-summary'), 'Fill in the STL dimension, the real-world dimension and a scale.');
        renderVerdict('c3-verdict', NaN);
        renderPanel('s3', null);
        return;
      }
      setText(out(root, 'c3-desired-mm'), C.fmtMm(r.desiredModelMm));
      setText(out(root, 'c3-desired-in'), C.fmtIn(r.desiredModelIn));
      setText(out(root, 'c3-percent'), C.fmtPercent(r.percent));
      setText(out(root, 'c3-pct-label'), C.fmtPercent(r.percent));
      var d3 = C.deltaPercent(r.percent);
      setText(out(root, 'c3-delta'), d3 ? d3.label : '');
      setText(out(root, 'c3-pair'), C.fmtMm(r.currentStlMm) + ' → ' + C.fmtMm(r.desiredModelMm) +
        ' at ' + C.fmtScale(C.parseScale(val(fld(root, 'c3-scale')))));
      setText(out(root, 'c3-work'), C.fmtMm(r.desiredModelMm, { bare: true }) + ' ÷ ' +
        C.fmtMm(r.currentStlMm, { bare: true }) + ' × 100 = ' + C.fmtPercent(r.percent));
      setText(out(root, 'c3-summary'), 'Set the slicer scale to ' + C.fmtPercent(r.percent) +
        ' on X, Y and Z. The model finishes ' + C.fmtMm(r.desiredModelMm) +
        ' (' + C.fmtIn(r.desiredModelIn) + ') along that axis.');
      renderVerdict('c3-verdict', r.desiredModelMm);
      root.__c3 = r;
      renderPanel('s3', {
        headline: C.fmtMm(r.currentStlMm) + ' STL → ' + C.fmtMm(r.desiredModelMm) +
                  ' at ' + C.fmtScale(C.parseScale(val(fld(root, 'c3-scale')))),
        percent: r.percent
      });
    }

    /* ---- CALC 4: estimate an unknown scale ----------------------------- */
    function calc4() {
      var est = C.estimateScale(
        val(fld(root, 'c4-real')), val(fld(root, 'c4-real-unit')),
        val(fld(root, 'c4-meas')), val(fld(root, 'c4-meas-unit')));
      var closeEl = out(root, 'c4-closest');
      var confEl = out(root, 'c4-confidence');
      // The model dimension the visitor measured is the one that has to print.
      renderVerdict('c4-verdict', C.toMm(val(fld(root, 'c4-meas')), val(fld(root, 'c4-meas-unit'))));
      if (!est) {
        setText(out(root, 'c4-scale'), '—');
        setText(closeEl, '');
        setText(confEl, '');
        return;
      }
      setText(out(root, 'c4-scale'), 'Approximately ' + est.label);
      setBrowse('c4', est.denominator);
      var cp = C.closestPreset(est.denominator);
      if (cp) {
        setText(closeEl, 'Closest common hobby scale: ' + C.presetLabel(cp.preset) +
          ' — difference ' + C.fmtPercent(cp.diffPercent) + '.');
        setText(confEl, cp.confidence + '. Your measurement stands on its own — ' +
          'nothing here is rounded to the nearest preset.');
        if (confEl) { confEl.setAttribute('data-mr-confidence', cp.confidence); }
      }
      root.__c4 = est;
    }

    /* ---- CALC 4b: image / building mode -------------------------------- */
    function calc5() {
      // Diagram labels track the inputs even before the numbers are complete.
      var refPxRaw = C.num(val(fld(root, 'c5-ref-px')));
      var tgtPxRaw = C.num(val(fld(root, 'c5-target-px')));
      var refRealRaw = C.num(val(fld(root, 'c5-ref-real')));
      var refUnitRaw = val(fld(root, 'c5-ref-unit')) || '';
      setText(out(root, 'c5-ref-px-label'),
        '\u2195 ' + (isFinite(refPxRaw) ? C.round(refPxRaw, 2) : '?') + ' pixels');
      setText(out(root, 'c5-target-px-label'),
        '\u2195 ' + (isFinite(tgtPxRaw) ? C.round(tgtPxRaw, 2) : '?') + ' pixels');
      setText(out(root, 'c5-ref-real-label'),
        (isFinite(refRealRaw) ? C.round(refRealRaw, 2) : '?') + ' ' + refUnitRaw + ' tall');

      var px = C.pixelReference(
        val(fld(root, 'c5-ref-real')), val(fld(root, 'c5-ref-unit')),
        val(fld(root, 'c5-ref-px')), val(fld(root, 'c5-target-px')));
      if (!px) {
        setText(out(root, 'c5-real-in'), '—');
        setText(out(root, 'c5-real-ft'), '—');
        setText(out(root, 'c5-real-ftin'), '—');
        setText(out(root, 'c5-real-m'), '—');
        setText(out(root, 'c5-model-mm'), '—');
        setText(out(root, 'c5-model-in'), '—');
        renderVerdict('c5-verdict', NaN);
        return;
      }
      setText(out(root, 'c5-real-in'), C.fmtIn(px.in));
      setText(out(root, 'c5-real-ft'), C.round(px.ft, 1) + ' ft');
      setText(out(root, 'c5-real-ftin'), C.fmtFeetInches(px.in));
      setText(out(root, 'c5-real-m'), C.round(px.m, 3) + ' m');
      var bScale = C.parseScale(val(fld(root, 'c5-scale')));
      setText(out(root, 'c5-scale-echo'), isFinite(bScale) ? C.fmtDenominator(bScale) : '—');
      var model = C.realToModel(px.mm, 'mm', val(fld(root, 'c5-scale')));
      setText(out(root, 'c5-model-mm'), model ? C.fmtMm(model.mm) : '—');
      setText(out(root, 'c5-model-in'), model ? C.fmtIn(model.in) : '—');
      renderVerdict('c5-verdict', model ? model.mm : NaN);
      setBrowse('c5', bScale);
      root.__c5 = px;
    }

    /* ---- browse-by-scale link ------------------------------------------ */

    // Quiet secondary link under a result: where to find models at that scale.
    function setBrowse(key, denom) {
      var el = root.querySelector('[data-mr-browse="' + key + '"]');
      var wrap = root.querySelector('[data-mr-browse-wrap="' + key + '"]') || el;
      var prompt = root.querySelector('[data-mr-browse-prompt="' + key + '"]');
      if (!el) { return; }
      var b = C.browseFor(denom);
      // ROUND 3 item 8: no destination for this scale means no link at all.
      if (!b) { if (wrap) { wrap.hidden = true; } return; }
      if (wrap) { wrap.hidden = false; }
      if (prompt) { setText(prompt, b.prompt); }
      el.href = b.url;
      setText(el, b.label + ' →');
    }

    /* ---- task tabs ------------------------------------------------------ */

    var TAB_MODE_EVENT = {
      resize: 'scale-to-scale', dimensions: 'real-to-model',
      identify: 'estimate-scale', photo: 'image-reference'
    };

    function tabs() { return $$(root, '[data-mr-tab]'); }

    // ROUND 3 item 15: one-click starting points. Each names its tab, the fields
    // it fills, and nothing else — the normal recompute path does the maths.
    var EXAMPLES = {
      'ho-to-o':       { mode: 'resize',     fields: { 'c1-from': '87', 'c1-to': '48' } },
      '35-to-72':      { mode: 'resize',     fields: { 'c1-from': '35', 'c1-to': '72' } },
      'door-ho':       { mode: 'dimensions', fields: { 'c2-l': '80', 'c2-unit': 'in', 'c2-scale': '87' }, focus: 'c2-l' },
      'door-identify': { mode: 'identify',
                         fields: { 'c4-ref-preset': 'door', 'c4-real': '80', 'c4-real-unit': 'in',
                                   'c4-meas': '23.36', 'c4-meas-unit': 'mm' },
                         focus: 'c4-meas' }
    };

    function runExample(key) {
      var ex = EXAMPLES[key];
      if (!ex) { return; }
      Object.keys(ex.fields).forEach(function (name) {
        var el = fld(root, name);
        if (el) { el.value = ex.fields[name]; }
      });
      showMode(ex.mode, { focusField: ex.focus });
      queueTrack();
    }

    function showMode(mode, opts) {
      var m = C.normalizeMode(mode) || 'resize';
      currentMode = m;
      lastMode = TAB_MODE_EVENT[m] || 'scale-to-scale';
      tabs().forEach(function (tab) {
        var on = tab.getAttribute('data-mr-tab') === m;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on && opts && opts.focus) { tab.focus(); }
      });
      $$(root, '[data-mr-mode-panel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-mr-mode-panel') !== m;
      });
      recompute();
      // ROUND 3 item 6: arriving at a task from a button lands on its first input.
      if (opts && opts.focusField) {
        var target = fld(root, opts.focusField);
        if (target) { try { target.focus(); target.select && target.select(); } catch (eF) {} }
      }
    }

    /* ---- slicer assistant ---------------------------------------------- */

    function panelEl(id, part) { return root.querySelector('[data-mr-panel="' + id + '"] ' + part); }

    // One panel per calculator that produces a resize percentage.
    // ctx = { headline, percent } or null when there is nothing to show.
    function renderPanel(id, ctx) {
      var wrap = root.querySelector('[data-mr-panel="' + id + '"]');
      if (!wrap) { return; }
      var chosen = C.getSlicer(slicerKey);

      if (!ctx || !isFinite(ctx.percent)) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;

      // Whichever calculator the visitor is actually using owns the analytics value.
      if ((id === 's3') === (lastMode === 'stl-to-percent')) { lastResize = ctx.percent; }


      var sev = C.resizeSeverity(ctx.percent);
      var sevEl = out(root, id + '-severity');
      if (sevEl) {
        setText(sevEl, sev ? sev.label : '');
        sevEl.setAttribute('data-mr-severity', sev ? sev.level : '');
      }

      var picker = panelEl(id, '[data-mr-picker]');
      var body = panelEl(id, '[data-mr-slicer-body]');

      if (!chosen) {
        // Nothing chosen yet: the panel asks once, inline.
        if (picker) { picker.hidden = false; }
        if (body) { body.hidden = true; }
        return;
      }

      if (body) { body.hidden = false; }
      if (picker && !picker.hasAttribute('data-mr-picker-open')) { picker.hidden = true; }

      setText(out(root, id + '-slicer-name'), 'In ' + chosen.name + ': ');
      setText(out(root, id + '-instruction'), C.instructionFor(chosen.key, ctx.percent));

      // ROUND 3 item 5: one help link per slicer. A verified vendor video when
      // there is one, otherwise the verified vendor documentation. Never both,
      // and never an embed.
      var docs = panelEl(id, '[data-mr-docs]');
      var video = panelEl(id, '[data-mr-video]');
      if (chosen.video) {
        if (video) {
          video.hidden = false;
          video.href = chosen.video;
          setText(video, 'Watch how to resize in ' + chosen.name + ' →');
        }
        if (docs) { docs.hidden = true; docs.removeAttribute('href'); }
      } else {
        if (video) { video.hidden = true; video.removeAttribute('href'); }
        if (docs) {
          docs.hidden = false;
          docs.href = chosen.docs;
          setText(docs, 'Read the ' + chosen.name + ' scaling guide →');
        }
      }

      // Warnings: the slicer's standing note, plus anything the resize size earns.
      var warnEl = out(root, id + '-warnings');
      if (warnEl) {
        var items = C.warningsFor(chosen.key, ctx.percent);
        var html = '';
        for (var i = 0; i < items.length; i++) {
          html += '<div class="mr-calc__warn mr-calc__warn--' + items[i].level + '">' +
                  '<strong>' + items[i].title + '</strong> ' + items[i].text + '</div>';
        }
        (chosen.warnings || []).forEach(function (w) {
          html += '<p class="mr-calc__warn-note">' + w + '</p>';
        });
        warnEl.innerHTML = html;
      }
    }

    function renderPanels() { calc1(); calc3(); }

    function setSlicer(key, opts) {
      var s = C.getSlicer(key);
      slicerKey = s ? s.key : '';
      $$(root, '[data-mr-slicer]').forEach(function (chip) {
        chip.classList.toggle('is-active', chip.getAttribute('data-mr-slicer') === slicerKey);
        chip.setAttribute('aria-pressed', chip.getAttribute('data-mr-slicer') === slicerKey ? 'true' : 'false');
      });
      if (s && !(opts && opts.silent)) {
        storeSlicer(s.key);
        track('slicer_selected', { slicer: s.key, source_page: sourcePage });
      }
      $$(root, '[data-mr-picker]').forEach(function (p) {
        if (s) { p.removeAttribute('data-mr-picker-open'); }
      });
      renderPanels();
    }

    function copyInstruction(id) {
      var chosen = C.getSlicer(slicerKey);
      var pctEl = out(root, id === 's3' ? 'c3-percent' : 'c1-percent');
      if (!chosen) { return ''; }
      return chosen.name + ' — resize to ' + (pctEl ? pctEl.textContent.trim() : '') + ': ' +
             ((out(root, id + '-instruction') || {}).textContent || '');
    }

    /* ---- mini converter ------------------------------------------------ */
    var convGuard = false;
    function convertMmIn(source) {
      if (convGuard) { return; }
      var mmEl = fld(root, 'conv-mm'), inEl = fld(root, 'conv-in');
      if (!mmEl || !inEl) { return; }
      convGuard = true;
      if (source === 'in') {
        var mm = C.convert(val(inEl), 'in', 'mm');
        mmEl.value = isFinite(mm) ? C.fmtMm(mm, { bare: true }) : '';
      } else {
        var inch = C.convert(val(mmEl), 'mm', 'in');
        inEl.value = isFinite(inch) ? C.fmtIn(inch, { bare: true }) : '';
      }
      convGuard = false;

      // ROUND 4 item 2: one plain equation per path, and nothing at all when
      // there is nothing to say.
      var mmNow = C.num(val(mmEl));
      var inNow = C.num(val(inEl));
      if (isFinite(mmNow) && isFinite(inNow)) {
        setText(out(root, 'conv-eq'), C.fmtMm(mmNow) + ' = ' + C.fmtIn(inNow));
      } else {
        setText(out(root, 'conv-eq'), '');
      }
      // Secondary line: only worth showing once the value is a foot or more.
      if (isFinite(mmNow) && Math.abs(mmNow) >= C.MM_PER.ft) {
        setText(out(root, 'conv-ftin'), C.fmtMm(mmNow) + ' = ' + C.fmtFeetInches(mmNow / C.MM_PER.in));
      } else {
        setText(out(root, 'conv-ftin'), '');
      }
    }

    function convertFeet() {
      var ftRaw = String(val(fld(root, 'conv-ft')) || '').trim();
      var inRaw = String(val(fld(root, 'conv-ft-in')) || '').trim();
      var target = out(root, 'conv-ft-mm');
      // Both fields empty: say nothing rather than half a sentence.
      if (ftRaw === '' && inRaw === '') { setText(target, ''); return; }
      var ft = C.num(ftRaw);
      var inch = C.num(inRaw);
      if (!isFinite(ft) && !isFinite(inch)) { setText(target, ''); return; }
      var totalIn = (isFinite(ft) ? ft * 12 : 0) + (isFinite(inch) ? inch : 0);
      var mm = totalIn * C.MM_PER.in;
      setText(target, C.fmtFeetInches(totalIn) + ' = ' + C.fmtMm(mm));
    }

    /* ---- quick reference table ----------------------------------------- */
    function quickRef() {
      var body = out(root, 'qr-body');
      if (!body) { return; }
      var value = val(fld(root, 'qr-value'));
      var unitKey = val(fld(root, 'qr-unit'));
      var rows = C.quickReference(value, unitKey);
      var caption = out(root, 'qr-caption');
      if (!rows.length) {
        body.innerHTML = '';
        setText(caption, 'Enter a real-world measurement to build the table.');
        return;
      }
      setText(caption, 'Model size of ' + C.num(value) + ' ' + unitKey + ' at each common scale.');
      var html = '';
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        var v = C.printability(r.mm);
        var cellCls = '', badge = '', note = '';
        if (v) {
          cellCls = v.tone === 'bad' ? ' class="mr-calc__cell--bad"'
                  : (v.tone === 'warn' ? ' class="mr-calc__cell--warn"' : '');
          badge = '<span class="mr-calc__verdict-badge is-' + v.tone + '">' + esc(v.label) + '</span>';
          note = v.reason ? '<span class="mr-calc__verdict-why">' + esc(v.reason) + '</span>' : '';
        }
        html += '<tr><th scope="row">' + r.label +
                (r.name ? ' <span class="mr-calc__tag">' + r.name + '</span>' : '') +
                '</th><td' + cellCls + '>' + C.fmtMm(r.mm) + '</td>' +
                '<td' + cellCls + '>' + C.fmtIn(r.in) + '</td>' +
                '<td class="mr-calc__cell--verdict">' + badge + note + '</td></tr>';
      }
      body.innerHTML = html;
    }

    /* ---- share URL ------------------------------------------------------ */
    function collectState() {
      function v(name) { var el = fld(root, name); return el ? String(el.value || '').trim() : ''; }
      return {
        mode: currentMode,
        from: v('c1-from'), to: v('c1-to'),
        real: v('c2-l'), ru: v('c2-unit'), scale: v('c2-scale'),
        stl: v('c3-stl'), su: v('c3-stl-unit'), rdim: v('c3-real'), rdu: v('c3-real-unit'),
        tscale: v('c3-scale'),
        ref: v('c4-real'), refu: v('c4-real-unit'), meas: v('c4-meas'), measu: v('c4-meas-unit'),
        prf: v('c5-ref-real'), prfu: v('c5-ref-unit'),
        dpx: v('c5-ref-px'), bpx: v('c5-target-px'), bscale: v('c5-scale'),
        q: v('qr-value'), qu: v('qr-unit'),
        slicer: slicerKey
      };
    }

    // Shared payload for the slicer-assistant events.
    function slicerPayload(extra) {
      var base = {
        calculator_mode: lastMode,
        slicer: slicerKey || null,
        resize_percent: isFinite(lastResize) ? C.round(lastResize, 2) : null,
        source_page: sourcePage
      };
      return extra ? Object.assign(base, extra) : base;
    }

    // ROUND 2 item 1: ordinary typing never touches the address bar. The URL is
    // built only when someone presses "Share result", and it carries just the
    // params of the mode they are in (plus the slicer, when one is chosen).
    // Which fields belong to which "Share result" button. The resize tab holds
    // two tools, so each shares only its own numbers.
    var SHARE_FIELDS = {
      s1: ['from', 'to'],
      s3: ['stl', 'su', 'rdim', 'rdu', 'tscale'],
      c2: ['real', 'ru', 'scale'],
      c4: ['ref', 'refu', 'meas', 'measu'],
      c5: ['prf', 'prfu', 'dpx', 'bpx', 'bscale']
    };

    // Which share-button groups belong to each task mode (used to prune a
    // legacy URL down to the fields that mode is allowed to carry).
    // Keys are the INTERNAL mode names (C.normalizeMode output / data-mr-tab):
    // resize, dimensions, identify, photo — not the public share aliases.
    var MODE_SHARE_IDS = {
      resize: ['s1', 's3'],
      dimensions: ['c2'],
      identify: ['c4'],
      photo: ['c5']
    };

    function shareUrl(mode, forId) {
      var state = collectState();
      var keep = SHARE_FIELDS[forId];
      if (keep) {
        var pruned = { slicer: state.slicer };
        keep.forEach(function (k) { pruned[k] = state[k]; });
        state = pruned;
      }
      var q = C.shareQuery(mode || currentMode, state);
      return window.location.origin + window.location.pathname + (q ? '?' + q : '');
    }

    // Called from the share button only — never from recompute().
    function pushShareUrl(mode, forId) {
      var url = shareUrl(mode, forId);
      try {
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', url);
        }
      } catch (e) {}
      return url;
    }

    function applyState(state) {
      function set(name, value) {
        if (value === undefined || value === '') { return; }
        var el = fld(root, name);
        if (!el) { return; }
        if (el.tagName === 'SELECT') {
          var found = Array.prototype.some.call(el.options, function (o) { return o.value === value; });
          if (found) { el.value = value; }
        } else {
          el.value = value;
        }
      }
      // ?from=87 arrives from product pages: it is the model's current scale.
      set('c1-from', state.from); set('c1-to', state.to);
      set('c2-l', state.real); set('c2-unit', state.ru); set('c2-scale', state.scale);
      set('c3-stl', state.stl); set('c3-stl-unit', state.su);
      set('c3-real', state.rdim); set('c3-real-unit', state.rdu); set('c3-scale', state.tscale);
      set('c4-real', state.ref); set('c4-real-unit', state.refu);
      set('c4-meas', state.meas); set('c4-meas-unit', state.measu);
      set('c5-ref-real', state.prf); set('c5-ref-unit', state.prfu);
      set('c5-ref-px', state.dpx); set('c5-target-px', state.bpx); set('c5-scale', state.bscale);
      set('qr-value', state.q); set('qr-unit', state.qu);
    }

    /* ---- analytics (debounced) ------------------------------------------ */
    var trackTimer = null;
    function queueTrack() {
      window.clearTimeout(trackTimer);
      trackTimer = window.setTimeout(function () {
        var payload = { calculator_mode: lastMode };
        if (lastMode === 'scale-to-scale') {
          payload.source_scale = C.parseScale(val(fld(root, 'c1-from'))) || null;
          payload.target_scale = C.parseScale(val(fld(root, 'c1-to'))) || null;
          payload.units = 'scale';
        } else if (lastMode === 'real-to-model') {
          payload.source_scale = null;
          payload.target_scale = C.parseScale(val(fld(root, 'c2-scale'))) || null;
          payload.units = val(fld(root, 'c2-unit')) || null;
        } else if (lastMode === 'stl-to-percent') {
          payload.source_scale = null;
          payload.target_scale = C.parseScale(val(fld(root, 'c3-scale'))) || null;
          payload.units = val(fld(root, 'c3-stl-unit')) || null;
        } else if (lastMode === 'estimate-scale') {
          payload.source_scale = null;
          payload.target_scale = root.__c4 ? C.round(root.__c4.denominator, 2) : null;
          payload.units = val(fld(root, 'c4-real-unit')) || null;
        } else if (lastMode === 'image-reference') {
          payload.source_scale = null;
          payload.target_scale = C.parseScale(val(fld(root, 'c5-scale'))) || null;
          payload.units = 'px';
        } else {
          payload.units = val(fld(root, 'qr-unit')) || null;
        }
        payload.resize_percent = isFinite(lastResize) ? C.round(lastResize, 2) : null;
        payload.slicer = slicerKey || null;
        payload.source_page = sourcePage;
        // No dimensions leave the browser — scales, percentages and unit names only.
        track('scale_calculation', payload);
      }, DEBOUNCE_MS);
    }

    /* ---- recompute ------------------------------------------------------ */
    function recompute() {
      calc1(); calc2(); calc3(); calc4(); calc5(); quickRef(); convertFeet();
    }

    var MODE_FOR = {
      c1: 'scale-to-scale', c2: 'real-to-model', c3: 'stl-to-percent',
      c4: 'estimate-scale', c5: 'image-reference', qr: 'quick-reference',
      conv: 'unit-converter'
    };

    function modeFromField(name) {
      if (!name) { return lastMode; }
      var prefix = name.split('-')[0];
      return MODE_FOR[prefix] || lastMode;
    }

    /* ---- events --------------------------------------------------------- */
    root.addEventListener('input', function (e) {
      var t = e.target;
      var name = t.getAttribute && t.getAttribute('data-mr-field');
      if (!name) { return; }
      if (name === 'conv-mm') { convertMmIn('mm'); return; }
      if (name === 'conv-in') { convertMmIn('in'); return; }
      lastMode = modeFromField(name);
      recompute();
      queueTrack();
    });

    root.addEventListener('change', function (e) {
      var t = e.target;
      var name = t.getAttribute && t.getAttribute('data-mr-field');
      if (!name) { return; }
      if (name === 'conv-mm' || name === 'conv-in') { convertMmIn(name === 'conv-in' ? 'in' : 'mm'); return; }
      if (name === 'slicer') { setSlicer(t.value); return; }
      lastMode = modeFromField(name);
      // Reference presets fill the known real size for the estimator.
      if (name === 'c4-ref-preset') {
        var opt = t.options[t.selectedIndex];
        if (opt && opt.getAttribute('data-size')) {
          var realEl = fld(root, 'c4-real'), unitEl = fld(root, 'c4-real-unit');
          if (realEl) { realEl.value = opt.getAttribute('data-size'); }
          if (unitEl) { unitEl.value = opt.getAttribute('data-unit') || 'in'; }
        }
      }
      if (name === 'c5-ref-preset') {
        var o2 = t.options[t.selectedIndex];
        if (o2 && o2.getAttribute('data-size')) {
          var r2 = fld(root, 'c5-ref-real'), u2 = fld(root, 'c5-ref-unit');
          if (r2) { r2.value = o2.getAttribute('data-size'); }
          if (u2) { u2.value = o2.getAttribute('data-unit') || 'in'; }
        }
      }
      recompute();
      queueTrack();
    });

    root.addEventListener('click', function (e) {
      var el = e.target.closest
        ? e.target.closest('[data-mr-action], [data-mr-preset], [data-mr-tab], [data-mr-slicer], [data-mr-example]')
        : null;
      if (!el || !root.contains(el)) { return; }

      var tabKey = el.getAttribute('data-mr-tab');
      if (tabKey) { showMode(tabKey); return; }

      var chipSlicer = el.getAttribute('data-mr-slicer');
      if (chipSlicer) { setSlicer(chipSlicer); return; }

      var exampleKey = el.getAttribute('data-mr-example');
      if (exampleKey) { runExample(exampleKey); return; }

      var presetVal = el.getAttribute('data-mr-preset');
      if (presetVal !== null) {
        var targetName = el.getAttribute('data-mr-preset-target');
        var targetEl = targetName ? fld(root, targetName) : null;
        if (targetEl) {
          targetEl.value = presetVal;
          lastMode = modeFromField(targetName);
          markActive(el);
          recompute();
          queueTrack();
        }
        return;
      }

      var action = el.getAttribute('data-mr-action');
      var group = el.getAttribute('data-mr-group');

      if (action === 'swap') {
        var aEl = fld(root, el.getAttribute('data-mr-a'));
        var bEl = fld(root, el.getAttribute('data-mr-b'));
        if (aEl && bEl) {
          var tmp = aEl.value; aEl.value = bEl.value; bEl.value = tmp;
          lastMode = modeFromField(el.getAttribute('data-mr-a'));
          recompute(); queueTrack();
        }
      } else if (action === 'reset') {
        $$(root, '[data-mr-field]').forEach(function (f) {
          if (group && f.getAttribute('data-mr-field').indexOf(group + '-') !== 0) { return; }
          if (f.tagName === 'SELECT') {
            var def = f.getAttribute('data-mr-default');
            if (def !== null) { f.value = def; }
          } else {
            f.value = f.getAttribute('data-mr-default') || '';
          }
        });
        $$(root, '.mr-calc__chip.is-active').forEach(function (c) {
          if (!group || (c.getAttribute('data-mr-preset-target') || '').indexOf(group + '-') === 0) {
            c.classList.remove('is-active');
          }
        });
        recompute();
      } else if (action === 'copy') {
        var srcName = el.getAttribute('data-mr-copy-source');
        var srcEl = srcName ? out(root, srcName) : null;
        copyText(srcEl ? srcEl.textContent.trim() : '', el);
      } else if (action === 'share' || action === 'copy-link') {
        var url = pushShareUrl(currentMode, el.getAttribute('data-mr-for'));
        copyText(url, el, 'Link copied');
        track('calculator_result_share', slicerPayload());
      } else if (action === 'share-page') {
        var pageUrl = window.location.origin + window.location.pathname;
        var shared = false;
        try {
          if (navigator.share) {
            navigator.share({
              title: document.title || '3D Printing Scale Calculator',
              url: pageUrl
            }).catch(function () {});
            shared = true;
          }
        } catch (eShare) {}
        if (!shared) { copyText(pageUrl, el, 'Link copied'); }
        track('calculator_result_share', slicerPayload({ share_target: 'page' }));
      } else if (action === 'find-scale') {
        showMode('identify', { focusField: 'c4-meas' });
      } else if (action === 'print') {
        try { window.print(); } catch (ePrint) {}
      } else if (action === 'copy-instruction') {
        var panelId = el.getAttribute('data-mr-for') || 's1';
        copyText(copyInstruction(panelId), el);
        track('slicer_instruction_copy', slicerPayload());
      } else if (action === 'change-slicer') {
        var pickerId = el.getAttribute('data-mr-for') || 's1';
        var pick = panelEl(pickerId, '[data-mr-picker]');
        if (pick) {
          var open = !pick.hidden;
          pick.hidden = open;
          if (open) { pick.removeAttribute('data-mr-picker-open'); }
          else {
            pick.setAttribute('data-mr-picker-open', '');
            var firstChip = pick.querySelector('[data-mr-slicer]');
            if (firstChip) { firstChip.focus(); }
          }
        }
      }
    });

    function markActive(chip) {
      var target = chip.getAttribute('data-mr-preset-target');
      $$(root, '[data-mr-preset-target="' + target + '"]').forEach(function (c) {
        c.classList.toggle('is-active', c === chip);
      });
    }

    // In-page jump nav: mark the current card for screen readers / styling.
    $$(root, '[data-mr-jump]').forEach(function (link) {
      link.addEventListener('click', function () {
        var id = link.getAttribute('href').replace('#', '');
        var target = document.getElementById(id);
        if (target) { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }
      });
    });

    // An external tutorial link is the only thing on this page that leaves it.
    root.addEventListener('click', function (e) {
      var v = e.target.closest ? e.target.closest('[data-mr-video]') : null;
      if (v && root.contains(v) && v.getAttribute('href')) {
        track('slicer_video_click', slicerPayload());
      }
    });

    // Roving-tabindex keyboard support for the task tablist.
    root.addEventListener('keydown', function (e) {
      var tab = e.target.closest ? e.target.closest('[data-mr-tab]') : null;
      if (!tab || !root.contains(tab)) { return; }
      var list = tabs();
      var i = list.indexOf(tab);
      var next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { next = (i + 1) % list.length; }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { next = (i - 1 + list.length) % list.length; }
      else if (e.key === 'Home') { next = 0; }
      else if (e.key === 'End') { next = list.length - 1; }
      if (next < 0) { return; }
      e.preventDefault();
      showMode(list[next].getAttribute('data-mr-tab'), { focus: true });
    });

    // Preload from the query string (?from=87&slicer=bambu&src=product and friends).
    var initialState = {};
    try { initialState = C.queryToState(window.location.search); } catch (e) {}
    try { applyState(initialState); } catch (e2) {}

    // URL wins over the remembered choice; both are written when one is picked.
    var initialSlicer = C.getSlicer(initialState.slicer) ? initialState.slicer : storedSlicer();
    if (C.getSlicer(initialSlicer)) {
      setSlicer(initialSlicer, { silent: true, skipUrl: true });
      if (C.getSlicer(initialState.slicer)) { storeSlicer(C.getSlicer(initialSlicer).key); }
    }

    // ?mode= picks the task; a product link (?from=…) lands on the resize tab.
    showMode(initialState.mode || 'resize');
    convertMmIn('mm');

    // 2026-08-25 — normalise a legacy/bloated URL in the address bar on load.
    // Older builds serialised EVERY calculator's fields into the query string;
    // links shared before that fix (and hand-edited ones) still carry them.
    // Rewrite to the clean equivalent for the active mode: the fields that mode
    // actually shares, plus slicer/src when present. Purely cosmetic —
    // applyState() has already run, so nothing about the result changes, and a
    // URL that is already clean is left untouched (no history entry churn).
    (function normaliseUrl() {
      try {
        if (!window.history || !window.history.replaceState) { return; }
        var search = window.location.search;
        if (!search || search.length < 2) { return; }
        var mode = currentMode; // already normalised by showMode() above
        if (!MODE_SHARE_IDS[mode]) { return; } // unknown mode: never blank a URL we don't understand
        var ids = MODE_SHARE_IDS[mode];
        // A mode can hold more than one tool (the resize tab has the scale-to-
        // scale pair and the "size it from a real measurement" disclosure).
        // A URL describes ONE calculation, so keep the first group the incoming
        // query actually populated and drop the rest.
        var group = null;
        for (var gi = 0; gi < ids.length && !group; gi++) {
          var fields = SHARE_FIELDS[ids[gi]] || [];
          for (var fi = 0; fi < fields.length; fi++) {
            if (initialState[fields[fi]] !== undefined && initialState[fields[fi]] !== '') { group = fields; break; }
          }
        }
        var keep = {};
        (group || []).forEach(function (k) { keep[k] = true; });
        // Only keep what the INCOMING url actually carried — never inject the
        // page's own defaults or the remembered slicer into the address bar.
        var pruned = {};
        Object.keys(initialState).forEach(function (k) {
          if (keep[k] && initialState[k] !== '' && initialState[k] != null) { pruned[k] = initialState[k]; }
        });
        if (initialState.slicer && C.getSlicer(initialState.slicer)) { pruned.slicer = initialState.slicer; }
        var hasFields = false;
        for (var kk in pruned) { if (Object.prototype.hasOwnProperty.call(pruned, kk)) { hasFields = true; break; } }
        var q = hasFields ? C.shareQuery(mode, pruned) : '';
        // Keep attribution parameters — utm_*, gclid, fbclid, src. Analytics and
        // ad platforms read these off the URL, and a campaign link that loses
        // them looks like direct traffic.
        var extra = [];
        if (initialState.src) { extra.push('src=' + encodeURIComponent(initialState.src)); }
        try {
          var raw = search.charAt(0) === '?' ? search.slice(1) : search;
          raw.split('&').forEach(function (pair) {
            if (!pair) { return; }
            var k = pair.split('=')[0];
            if (/^utm_/i.test(k) || k === 'gclid' || k === 'fbclid' || k === 'msclkid' || k === 'ttclid') { extra.push(pair); }
          });
        } catch (e2) {}
        if (extra.length) { q = q ? q + '&' + extra.join('&') : extra.join('&'); }
        var clean = window.location.pathname + (q ? '?' + q : '');
        if (clean !== window.location.pathname + search) {
          window.history.replaceState(null, '', clean);
        }
      } catch (e) {}
    })();

    return {
      root: root,
      recompute: recompute,
      shareUrl: shareUrl,
      applyState: applyState,
      showMode: showMode,
      setSlicer: setSlicer
    };
  }

  function mountAll() {
    $$(document, '[data-mr-calc]').forEach(mount);
  }

  window.MRScaleCalcUI = { mount: mount, mountAll: mountAll, track: track };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }
})();
